import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {UsersService} from '../../../../services/users/users.service';
import {TasksService} from '../../../../services/tasks/tasks.service';
import {GoalsService} from '../../../../services/goals/goals.service';
import {MatSnackBar} from '@angular/material/snack-bar';

@Component({
    selector: 'app-superadmin-claimed-reward',
    templateUrl: './superadmin-claimed-reward.component.html',
    styleUrls: ['./superadmin-claimed-reward.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuperadminClaimedRewardComponent implements OnInit {
  public rewardId = '';
  public columnsToDisplay: string[] = ['createdAt', 'status', 'user', 'taskGoal', 'actions'];
  public items: any[] = [];
  public loading = false;
  public page = 1;
  public limit = 30;
  public endOfList = false;
  private readonly statusCycle = ['presented', 'claimed', 'active'];

  public userCache: Record<string, string> = {};
  public goalTaskCache: Record<string, string> = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private rewardsAdminService: RewardsAdminService,
    private usersService: UsersService,
    private tasksService: TasksService,
    private goalsService: GoalsService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.rewardId = this.route.snapshot.paramMap.get('rewardId') || '';
    this.loadItems(true);
  }

  async loadItems(clear = false): Promise<void> {
    if (clear) {
      this.page = 1;
      this.endOfList = false;
      this.items = [];
    }
    if (this.loading || this.endOfList) {
      return;
    }
    this.loading = true;
    try {
      const claimed = await this.rewardsAdminService.getClaimedRewards('', this.rewardId, this.page, this.limit);
      if (!claimed || claimed.length === 0) {
        this.endOfList = true;
      } else {
        this.items = this.items.concat(claimed);
        this.page += 1;
        claimed.forEach((reward) => this.populateCaches(reward));
      }
    } catch (error) {
      console.error('Error loading claimed rewards', error);
    } finally {
      this.loading = false;
    }
  }

  async populateCaches(reward: any): Promise<void> {
    if (reward.taskId && !this.goalTaskCache[reward.taskId]) {
      try {
        const task = await this.tasksService.getTaskById(reward.userId, reward.taskId);
        this.goalTaskCache[reward.taskId] = task.title || task.description || task.specific || '';
      } catch {
        this.goalTaskCache[reward.taskId] = '';
      }
    }
    if (reward.goalId && !this.goalTaskCache[reward.goalId]) {
      try {
        const goal = await this.goalsService.getGoalById(reward.goalId);
        this.goalTaskCache[reward.goalId] = `${goal.title} $${goal.cost}`;
      } catch {
        this.goalTaskCache[reward.goalId] = '';
      }
    }
    if (!this.userCache[reward.userId]) {
      try {
        const user = await this.usersService.getUserById(reward.userId);
        this.userCache[reward.userId] = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      } catch {
        this.userCache[reward.userId] = 'Old User';
      }
    }
  }

  cancel(): void {
    this.router.navigate(['/superadmin/rewards/rewards']).then();
  }

  async cycleStatus(event: Event, item: any): Promise<void> {
    event.stopPropagation();
    const currentStatus = (item?.status || '').toLowerCase();
    const nextStatus = this.statusCycle[(this.statusCycle.indexOf(currentStatus) + 1) % this.statusCycle.length];
    const payload = { ...item, status: nextStatus };
    try {
      await this.rewardsAdminService.putClaimedReward(payload);
      item.status = nextStatus;
      this.snackBar.open(`Claimed reward status updated to ${nextStatus}`, 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error cycling claimed reward status', error);
      this.snackBar.open('Unable to update claimed reward status', 'Close', { duration: 3000 });
    }
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    const threshold = 150;
    if (scrollTop + offsetHeight >= scrollHeight - threshold) {
      this.loadItems();
    }
  }
}
